import { gsap } from 'gsap';

/**
 * Individuals Slider Module
 *
 * Gère les sliders pour les membres de l'équipe.
 * Copié et adapté depuis TestimonialsSlider.
 */
class IndividualsSlider {
    constructor(container) {
        this.container = container;
        this.wrapper = container.querySelector('[data-individuals-container]');
        this.slides = container.querySelectorAll('[data-individual-item]');
        
        const sliderParent = container.closest('.individuals-carousel');
        this.prevBtn = sliderParent.querySelector('.individuals-slider-prev');
        this.nextBtn = sliderParent.querySelector('.individuals-slider-next');
        this.pagination = sliderParent.querySelector('[data-individuals-pagination]');

        this.currentIndex = 0;
        this.slidesPerView = this.getSlidesPerView();
        this.totalSlides = this.slides.length;
        this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);

        this.init();
    }

    init() {
        if (!this.wrapper || this.slides.length === 0) {
            console.warn('Individuals slider not initialized: wrapper or slides missing.');
            return;
        }
        this.setupSlider();
        this.createPagination();
        this.bindEvents();
        this.updateNavigation();
    }

    setupSlider() {
        this.slides.forEach(slide => {
            slide.style.flexShrink = '0';
        });
        this.updateSlidePosition();
    }

    getSlidesPerView() {
        const width = window.innerWidth;
        if (width < 640) return 1;   // Mobile
        if (width < 1024) return 2;  // Tablette
        return 3;                    // Desktop
    }

    createPagination() {
        if (!this.pagination || this.maxIndex < 1) return;

        this.pagination.innerHTML = '';
        // On ne crée des points que s'il y a plus d'une "page" de slides
        const pageCount = this.maxIndex + 1;

        for (let i = 0; i < pageCount; i++) {
            const dot = document.createElement('button');
            dot.className = 'w-2.5 h-2.5 bg-gray-300 rounded-full cursor-pointer transition-all duration-300 hover:bg-orange-400';
            dot.dataset.index = i;
            dot.setAttribute('aria-label', `Go to page ${i + 1}`);
            this.pagination.appendChild(dot);
        }
        this.updatePagination();
    }

    bindEvents() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1));
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1));
        }
        if (this.pagination) {
            this.pagination.addEventListener('click', (e) => {
                if (e.target.matches('button[data-index]')) {
                    this.goTo(parseInt(e.target.dataset.index));
                }
            });
        }
        window.addEventListener('resize', this.onResize.bind(this));
    }

    goTo(index) {
        this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
        this.updateNavigation();
        this.updateSlidePosition();
        this.updatePagination();
    }

    updateNavigation() {
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentIndex === 0;
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
        }
    }

    updatePagination() {
        if (!this.pagination) return;
        const dots = this.pagination.querySelectorAll('button');
        dots.forEach((dot, index) => {
            if (index === this.currentIndex) {
                dot.classList.remove('bg-gray-300', 'w-2.5');
                dot.classList.add('bg-orange-500', 'w-6');
            } else {
                dot.classList.remove('bg-orange-500', 'w-6');
                dot.classList.add('bg-gray-300', 'w-2.5');
            }
        });
    }

    updateSlidePosition() {
        // La largeur du slide est calculée à partir de la grille Tailwind
        const slideWidth = this.slides[0].offsetWidth;
        const gap = parseInt(window.getComputedStyle(this.wrapper).gap) || 32; // 32px = gap-8
        const offset = -this.currentIndex * (slideWidth + gap);

        gsap.to(this.wrapper, {
            x: offset,
            duration: 0.8,
            ease: 'power3.out'
        });
    }

    onResize() {
        const newSlidesPerView = this.getSlidesPerView();
        if (newSlidesPerView !== this.slidesPerView) {
            this.slidesPerView = newSlidesPerView;
            this.maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
            this.createPagination();
            this.goTo(this.currentIndex); // Recalculate position and nav
        } else {
            // Just update position in case of width changes without breakpoint change
            this.updateSlidePosition();
        }
    }
}

export default IndividualsSlider;
